import * as fromRouter from '@ngrx/router-store';

import { ActionReducer, ActionReducerMap, combineReducers, compose, createSelector } from '@ngrx/store';
import { storeLogger } from 'ngrx-store-logger';

import { environment } from '../../../../environments/environment';
import * as fromAppState from './app';
import * as fromApiKeys from './api-key';
import * as fromLogin from './login';
import * as fromConfig from './config';
import * as fromUraniaAdmin from './urania-admin';
import * as fromDisabled from './disabled';
import * as fromSidebar from './sidebar';
import * as fromSearchMask from './searchmask';

export interface State {
	app: fromAppState.State;
	routerReducer: fromRouter.RouterReducerState;
	apiKeys: fromApiKeys.State;
	login: fromLogin.State;
	config: fromConfig.State;
	uraniaAdmin: fromUraniaAdmin.State;
	disabled: fromDisabled.State;
	sidebar: fromSidebar.State;
	searchMask: fromSearchMask.State;
}

export const reducers: ActionReducerMap<State> = {
	app: fromAppState.reducer,
	routerReducer: fromRouter.routerReducer,
	apiKeys: fromApiKeys.reducer, login: fromLogin.reducer,
	config: fromConfig.reducer,
	uraniaAdmin: fromUraniaAdmin.reducer,
	disabled: fromDisabled.reducer,
	sidebar: fromSidebar.reducer,
	searchMask: fromSearchMask.reducer,
};

export const FEATURE_STATE_NAME = 'xena';

/**
 * not needed currently, leave in here as reference
 */
export function rootReducer(reducer: ActionReducer<any>): ActionReducer<any> {
	return function (state, action) {
		return reducer(state, action);
	};
}

export function ipcChannelReducer(reducer: ActionReducer<any>): ActionReducer<any> {
	return function (state, action) {
		if (window && window['ipc'] && window['ipc']['send']) {
			window['ipc'].send('actionchannel', action);
		}

		return reducer(state, action);
	};
}

export function reducerFactory(appReducers: any) {
	return environment.production
		? compose(ipcChannelReducer, combineReducers)(appReducers)
		: compose(storeLogger({collapsed: true}), rootReducer, ipcChannelReducer, combineReducers)(appReducers);
}



export const getAppState = (s: State): fromAppState.State => s.app;
export const getRouterState = (s: State): fromRouter.RouterReducerState => s.routerReducer;
export const getApiKeys = (s: State): fromApiKeys.State => s.apiKeys;
export const getLoginState = (s: State): fromLogin.State => s.login;
export const getConfig = (s: State): fromConfig.State => s.config;
export const getUraniaAdmin = (s: State): fromUraniaAdmin.State => s.uraniaAdmin;
export const getDisabled = (s: State): fromDisabled.State => s.disabled;
export const getSidebar = (s: State): fromSidebar.State => s.sidebar;
export const getSearchMask = (s: State): fromSearchMask.State => s.searchMask;
export const getComponents = createSelector(getConfig, fromConfig.GetXenaComponents);
export const getSelectedLanguage = createSelector(getAppState, fromAppState.GetSelectedLanguage);
export const getAgencies = createSelector(getAppState, fromAppState.GetAgencies);
export const getSelectedAgency = createSelector(getAppState, fromAppState.GetSelectedAgency);
export const getSSOSelectedAgencyId = createSelector(getAppState, fromAppState.GetSSOSelectedAgencyId);
export const getSelectedProvider = createSelector(getAppState, fromAppState.GetSelectedProvider);
export const showNeoFrame = createSelector(getAppState, fromAppState.ShowNeoFrame);
export const showARGUSFrame = createSelector(getAppState, fromAppState.ShowARGUSFrame);
export const getUserId = createSelector(getLoginState, fromLogin.GetUserId);
export const getUser = createSelector(getLoginState, fromLogin.GetUser);
export const getNeoConfig = createSelector(getConfig, fromConfig.GetNeoConfig);
export const getNeoConfigRev = createSelector(getConfig, fromConfig.GetNeoConfigRev);
export const getXenaTempConfig = createSelector(getConfig, fromConfig.GetTempConfig);
export const getXenaConfig = createSelector(getConfig, fromConfig.GetXenaConfig);
export const getXenaGlobalConfig = createSelector(getConfig, fromConfig.GetXenaGlobalConfig);
export const getUraniaConfig = createSelector(getConfig, fromConfig.GetUraniaConfig);
export const getXenaConfigRev = createSelector(getConfig, fromConfig.GetXenaConfigRev);
export const getXenaGlobalConfigRev = createSelector(getConfig, fromConfig.GetXenaGlobalConfigRev);
export const getUraniaConfigRev = createSelector(getConfig, fromConfig.GetUraniaConfigRev);
export const getSelectedUraniaCfg = createSelector(getUraniaAdmin, fromUraniaAdmin.GetSelectedUraniaCfg);
export const getNEODisabledState = createSelector(getDisabled, fromDisabled.neoDisabledState);
export const getXENADisabledState = createSelector(getDisabled, fromDisabled.xenaDisabledState);
export const hideXenaNeoSwitch = createSelector(getDisabled, fromDisabled.hideXenaNeoStwitch);
export const urlaubsIdeeDisabledState = createSelector(getDisabled, fromDisabled.urlaubsIdeeDisabledState);
export const getActiveNavigationItem = createSelector(getSidebar, fromSidebar.GetActiveNavigationItem);
export const getActiveNavigationMenuItem = createSelector(getSidebar, fromSidebar.GetActiveNavigationMenuItem);
export const isNavigationExpanded = createSelector(getSidebar, fromSidebar.IsNavigationExpanded);
export const isAdminSectionExpanded = createSelector(getSidebar, fromSidebar.IsAdminSectionExpanded);
export const getSearchMasks = createSelector(getSearchMask, fromSearchMask.GetAll);
export const getSelectedSearchMask = createSelector(getSearchMask, fromSearchMask.GetSelected);

export const isAllowedUserIfTMG = createSelector(getAppState, getLoginState, (appState, loginState) => {
	const selectedAgency = fromAppState.GetSelectedAgency(appState);
	const userId = fromLogin.GetUserId(loginState);

	if (!selectedAgency || !userId) {
		return false;
	}

	if (selectedAgency.agentId !== 23031) {
		return true;
	}

	return [95198, 128384, 98313].includes(userId);
});

export const isTmgAdminEnabled = createSelector(getAppState, getLoginState, (appState, loginState) => {
	const selectedAgency = fromAppState.GetSelectedAgency(appState);
	const userId = fromLogin.GetUserId(loginState);

	if (!selectedAgency || !userId) {
		return false;
	}

	return selectedAgency.agentId === 23031;
});

export const isBosys = createSelector(getAppState, getLoginState, (appState, loginState) => {
	const selectedAgency = fromAppState.GetSelectedAgency(appState);
	const userId = fromLogin.GetUserId(loginState);

	if (!selectedAgency || !userId) {
		return false;
	}

	return [2, 50, 36].includes(selectedAgency.providerId)
		|| [101879, 107011, 113336, 120674, 18829, 22243, 141596, 141735, 112942, 112773, 112730, 102369, 113405, 102009, 102481, 113406, 113658, 102085, 112863, 112789, 101589, 101917, 103020, 101509, 101509].includes(selectedAgency.agentId);
});

export const isSchmetterling =  createSelector(getAppState, getLoginState, (appState, loginState) => {
	const selectedAgency = fromAppState.GetSelectedAgency(appState);
	const userId = fromLogin.GetUserId(loginState);

	if (!selectedAgency || !userId) {
		return false;
	}

	return selectedAgency.providerId === 1;
});

export const openNEOInNewTab = createSelector(getXenaConfig, config => {
	return config && config.presetsConfiguration && config.presetsConfiguration.openNeoInNewTab || false;
});



// WEBPACK FOOTER //
// ./src/app/shared/state-management/reducers/index.ts